import React, { createContext, useState, useEffect } from 'react';
import TokenService from '../services/tokenService';
import { BASE_WS_URL } from '../constants/index';

const WebSocketContext = createContext();

export const WebSocketProvider = ({ children }) => {
  const [needsRefresh, setNeedsRefresh] = useState(0);
  const [webSocket, setWebSocket] = useState({
    socket: null,
    state: { connected: false, error: null },
  });
  const reconnectTimeoutRef = React.useRef(null);
  const tokenCheckIntervalRef = React.useRef(null);

  // reconnect (fire-and-forget)
  const reconnect = (setWebSocket) => {
    if (!setWebSocket) {
      throw new Error('WebSocket context is not initialized yet.');
    }
    console.log('Reconnecting WebSocket...');
    
    // Clear any existing reconnect timeout
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    
    openSocket(setWebSocket);
  };

  // sendMessage
  const sendMessage = (message) => {
    if (!webSocket.socket || webSocket.socket.readyState !== WebSocket.OPEN) {
      console.log('WebSocket is not connected or ready. Current state:', webSocket.socket?.readyState);
      return;
    }
    try {
      const messageString = JSON.stringify(message);
      webSocket.socket.send(messageString);
      console.log(`Message sent: ${JSON.stringify(message)}`);
    } catch (error) {
      console.error('Error sending message:', error);
    }
  };

  // openSocket (no promise return)
  const openSocket = (setWebSocket) => {
    let token;
    if ((token = TokenService.getLocalAccessToken())) {
      console.log('openSocket: Access token found, validating...');
      if (!TokenService.isValidToken(token)) {
        console.error('openSocket: Invalid or expired token. Refreshing...');
        refreshAndConnect(setWebSocket);
        return;
      }
      console.log('openSocket: Token is valid, opening WebSocket connection...');
      try {
        // Close any existing socket before creating a new one
        setWebSocket((prevState) => {
          if (prevState.socket && prevState.socket.readyState !== WebSocket.CLOSED) {
            console.log('Closing existing WebSocket before reconnecting...');
            prevState.socket.close();
          }
          return prevState;
        });

        const socket = new WebSocket(`${BASE_WS_URL}?token=${token}`);
        setWebSocket((prevState) => ({
          ...prevState,
          socket,
        }));

        socket.onopen = () => {
          console.log('WebSocket connected!');
          setWebSocket((prevState) => ({
            ...prevState,
            state: { connected: true, error: null },
          }));
          setupEventListeners(socket);
          
          // Start periodic token validation check
          startTokenCheck(setWebSocket);
        };

        const setupEventListeners = (socket) => {
          socket.onerror = (error) => {
            console.log('WebSocket error:', error);
            // Don't try to use the socket after an error
          };
          
          socket.onclose = (event) => {
            console.log('WebSocket closed:', event.code, event.reason);
            setWebSocket((prevState) => ({
              ...prevState,
              state: { connected: false, error: 'Connection closed' },
            }));
            
            // Clear any existing reconnect timeout
            if (reconnectTimeoutRef.current) {
              clearTimeout(reconnectTimeoutRef.current);
            }
            
            // Only reconnect if it wasn't a clean close
            if (event.code !== 1000) {
              reconnectTimeoutRef.current = setTimeout(() => {
                reconnectTimeoutRef.current = null;
                reconnect(setWebSocket);
              }, 5000);
            }
          };
          
          socket.onmessage = (event) => {
            try {
              const data = JSON.parse(event.data);
              if (data.message === 'event-refresh') {
                console.log('Refresh event list triggered!', data.data);
                setNeedsRefresh((value) => value + 1);
              }
            } catch (error) {
              console.error('Error parsing WebSocket message:', error);
            }
          };
        };
      } catch (error) {
        console.error('Error creating WebSocket:', error);
      }
    } else {
      console.error('No access token found');
    }
  };

  // Proactively check token validity and refresh before expiration
  const startTokenCheck = (setWebSocket) => {
    // Clear any existing interval
    if (tokenCheckIntervalRef.current) {
      clearInterval(tokenCheckIntervalRef.current);
    }
    
    // Check token every 5 minutes
    tokenCheckIntervalRef.current = setInterval(() => {
      const token = TokenService.getLocalAccessToken();
      
      if (!token || !TokenService.isValidToken(token)) {
        console.log('Token expired during session, reconnecting...');
        clearInterval(tokenCheckIntervalRef.current);
        tokenCheckIntervalRef.current = null;
        
        // Close current socket before refreshing
        setWebSocket((prevState) => {
          if (prevState.socket && prevState.socket.readyState !== WebSocket.CLOSED) {
            try {
              prevState.socket.close();
            } catch (e) {
              console.error('Error closing WebSocket:', e);
            }
          }
          return {
            socket: null,
            state: { connected: false, error: null },
          };
        });
        
        refreshAndConnect(setWebSocket);
      }
    }, 5 * 60 * 1000); // Check every 5 minutes
  };

  // refreshAndConnect (simplified with async/await)
  const refreshAndConnect = async (setWebSocket) => {
    try {
      const refreshToken = TokenService.getLocalRefreshToken();
      if (!refreshToken) {
        console.error('No refresh token found. Cannot refresh access token.');
        return;
      }

      console.log('Attempting to refresh token for WebSocket connection...');
      const newAccessToken = await TokenService.refreshAccessToken(refreshToken);
      console.log('Token refresh result:', newAccessToken ? 'Success' : 'Failed');
      
      if (newAccessToken) {
        console.log('Reconnecting WebSocket with new token...');
        // Small delay to ensure localStorage is updated before reconnecting
        await new Promise(resolve => setTimeout(resolve, 100));
        
        setWebSocket((prevState) => ({
          ...prevState,
          socket: null,
          state: { connected: false, error: null },
        }));
        openSocket(setWebSocket); // reconnect with new token
      } else {
        console.error('Failed to refresh access token - no token returned');
      }
    } catch (error) {
      console.error('Error refreshing access token for WebSocket:', error);
      console.error('Error details:', error.response?.data || error.message);
    }
  };

  useEffect(() => {
    openSocket(setWebSocket);
    
    // Cleanup function to close WebSocket and clear timeouts
    return () => {
      console.log('Cleaning up WebSocket connection...');
      
      // Clear reconnect timeout if pending
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
      
      // Clear token check interval if running
      if (tokenCheckIntervalRef.current) {
        clearInterval(tokenCheckIntervalRef.current);
        tokenCheckIntervalRef.current = null;
      }
      
      // Close WebSocket connection
      setWebSocket((prevState) => {
        if (prevState.socket && prevState.socket.readyState !== WebSocket.CLOSED) {
          try {
            prevState.socket.close();
            console.log('WebSocket closed on cleanup');
          } catch (e) {
            console.error('Error closing WebSocket during cleanup:', e);
          }
        }
        return {
          socket: null,
          state: { connected: false, error: null },
        };
      });
    };
    // eslint-disable-next-line
  }, []);

  return (
    <WebSocketContext.Provider
      value={{ ...webSocket, sendMessage, reconnect, needsRefresh, setNeedsRefresh }}
    >
      {children}
    </WebSocketContext.Provider>
  );
};

export default WebSocketContext;
